import {Component, Input, OnInit} from '@angular/core';
import {Structure} from 'src/app/shared/models/entities/structure';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {imageFileUploadOptions} from 'src/app/configs/file-upload.config';
import {GoogleMapsUtil} from 'src/app/shared/utils/google-maps.util';

@Component({
  selector: 'laveo-structure-form',
  templateUrl: './structure-form.component.html',
  styleUrls: ['./structure-form.component.scss']
})
export class StructureFormComponent implements OnInit {
  @Input() structure?: Structure;

  form: UntypedFormGroup;
  fileUploadOptions: any = {};

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    public readonly googleMapsUtil: GoogleMapsUtil
  ) {}

  get contactFormArray(): UntypedFormArray {
    return this.form.get('contacts') as UntypedFormArray;
  }

  ngOnInit(): void {
    this.setForm();
    this.fileUploadOptions = imageFileUploadOptions;
    this.googleMapsUtil.load();
  }

  onFullAddressChange(address: google.maps.places.PlaceResult): void {
    if (!address) {
      return;
    }

    const streetNumber = address.address_components?.find(x => x.types.includes('street_number'));
    const street = address.address_components?.find(x => x.types.includes('route'));
    const postalCode = address.address_components?.find(x => x.types.includes('postal_code'));
    const city = address.address_components?.find(x => x.types.includes('locality'));
    const country = address.address_components?.find(x => x.types.includes('country'));
    const latitude = address?.geometry?.location?.lat();
    const longitude = address?.geometry?.location?.lng();

    if (street && streetNumber) {
      this.form.get('address')?.setValue(streetNumber.long_name + ' ' + street.long_name);
    } else if (street) {
      this.form.get('address')?.setValue(street.long_name);
    } else {
      this.form.get('address')?.setValue(null);
    }
    if (postalCode) {
      this.form.get('postalCode')?.setValue(postalCode.long_name);
    } else {
      this.form.get('postalCode')?.setValue(null);
    }
    if (city) {
      this.form.get('city')?.setValue(city.long_name);
    } else {
      this.form.get('city')?.setValue(null);
    }
    if (country) {
      this.form.get('country')?.setValue(country.short_name);
    } else {
      this.form.get('country')?.setValue(null);
    }
    if (latitude && longitude) {
      this.form.get('latitude')?.setValue(latitude);
      this.form.get('longitude')?.setValue(longitude);
    } else {
      this.form.get('latitude')?.setValue(null);
      this.form.get('longitude')?.setValue(null);
    }

    this.form.updateValueAndValidity();
  }

  preparers(structure: Structure): string {
    if (structure?.preparersMetaData?.totalResults > 0) {
      return structure.preparersMetaData?.totalResults > 1 ? structure.preparersMetaData?.totalResults + ' préparateurs associés' : structure.preparersMetaData?.totalResults + ' préparateur associé';
    }
    return 'Aucun préparateur associé';
  }

  customerSites(structure: Structure): string {
    if (structure?.customerSitesMetaData?.totalResults > 0) {
      return structure.customerSitesMetaData?.totalResults > 1 ? structure.customerSitesMetaData?.totalResults + ' sites associés' : structure.customerSitesMetaData?.totalResults + ' site associé';
    }
    return 'Aucun site associé';
  }

  addLogo(event: { output: File }): void {
    const logoInput = this.form.get('logo');
    logoInput?.setValue(event.output);
    logoInput?.updateValueAndValidity();
  }

  removeLogo(): void {
    const logoInput = this.form.get('logo');
    logoInput?.setValue(null);
    logoInput?.updateValueAndValidity();
  }

  private setForm(): void {
    this.form = this.formBuilder.group({
      id: this.formBuilder.control(this.structure?.id, []),
      active: this.formBuilder.control(this.structure?.active ?? true, []),
      name: this.formBuilder.control(this.structure?.name, [Validators.required]),
      name2: this.formBuilder.control(this.structure?.name2, [Validators.required]),
      fullAddress: this.formBuilder.control(this.structure?.fullAddress, []),
      address: this.formBuilder.control(this.structure?.address, []),
      city: this.formBuilder.control(this.structure?.city, []),
      postalCode: this.formBuilder.control(this.structure?.postalCode, []),
      country: this.formBuilder.control(this.structure?.country, []),
      latitude: this.formBuilder.control(this.structure?.latitude, []),
      longitude: this.formBuilder.control(this.structure?.longitude, []),
      contacts: this.formBuilder.array(this.structure?.contacts ?? [], []),
      logo: this.formBuilder.control(null, []),
      hasVat: this.formBuilder.control(this.structure?.hasVat, []),
      isAutoInvoiceEnabled : this.formBuilder.control(this.structure?.isAutoInvoiceEnabled, []),
      registrationId : this.formBuilder.control(this.structure?.registrationId, []),
      vatNumber : this.formBuilder.control(this.structure?.vatNumber, []),
      legalStatus : this.formBuilder.control(this.structure?.legalStatus, []),
      shareCapital : this.formBuilder.control(this.structure?.shareCapital, []),
      rcsNumber : this.formBuilder.control(this.structure?.rcsNumber, []),
      rcsCity : this.formBuilder.control(this.structure?.rcsCity, []),
      nafCode : this.formBuilder.control(this.structure?.nafCode, []),
      otherInvoiceInfo : this.formBuilder.control(this.structure?.otherInvoiceInfo, []),
    });
  }
}
